import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  Badge,
  Alert,
  Spinner,
  Dropdown,
} from "react-bootstrap";
import ResizableTable from "../common/ResizableTable";
import {
  Calendar3,
  Filter,
  Search,
  FileEarmarkText
} from "react-bootstrap-icons";
import DataApi from "../../api/dataApi";
import PubSub from "pubsub-js";
import { format, parseISO } from 'date-fns';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const InvoicesReport = ({ permissions }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ records: [], stats: {}, total_records: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Main API Filters
  const [filters, setFilters] = useState({
    days: "30",
    from_date: "",
    to_date: "",
    status: "",
    searchTerm: "",
  });

  // Local state for the search input to handle debouncing
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [todayStr] = useState(new Date().toISOString().split("T")[0]);

  // Styles matching BookPenaltyReport
  const labelStyle = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#495057',
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const inputBaseStyle = {
    height: '44px',
    borderRadius: '8px',
    border: '1.5px solid #dee2e6',
    padding: '0.625rem 1rem',
    fontSize: '0.9375rem',
    transition: 'all 0.2s ease',
    backgroundColor: '#fff',
    width: '100%'
  };

  // Debounce effect for search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length === 0 || searchInput.length >= 3) {
        setFilters(prev => ({
          ...prev,
          searchTerm: searchInput
        }));
        setCurrentPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch Report Data whenever filters change
  useEffect(() => {
    fetchReport();
  }, [filters]);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const api = new DataApi("reports");
      const params = new URLSearchParams();

      // Explicitly build the query string based on filters
      if (filters.days === "custom") {
        params.append("days", "custom");
        if (filters.from_date) params.append("from_date", filters.from_date);
        if (filters.to_date) params.append("to_date", filters.to_date);
      } else {
        params.append("days", filters.days);
      }

      if (filters.status) params.append("status", filters.status);
      if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);

      // Call API using the correctly formatted query string
      const resp = await api.get(`/invoices?${params.toString()}`);
      setData(resp.data || resp);

    } catch (err) {
      console.error("Error fetching invoices:", err);
      setError("Failed to load invoice report. Please try again.");
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Failed to load invoices"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      ...(field === "days" && value !== "custom" ? { from_date: "", to_date: "" } : {})
    }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchInput("");
    setFilters({
      days: "30",
      from_date: "",
      to_date: "",
      status: "",
      searchTerm: "",
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'UNPAID': 'danger',
      'PARTIAL': 'warning',
      'PAID': 'success',
      'CANCELLED': 'secondary'
    };
    return badges[status] || 'secondary';
  };

  // Table Columns for ResizableTable
  const columns = [
    { field: "invoice_number", label: "Invoice #", width: "150px", align: "left", render: (val) => <span className="fw-semibold">{val}</span> },
    { field: "invoice_date", label: "Date", width: "120px", render: (val) => val ? format(parseISO(val), 'MMM dd, yyyy') : '-' },
    { field: "vendor_name", label: "Vendor", width: "200px" },
    { field: "total_amount", label: "Total", width: "130px", align: "right", render: (val) => `₹${Number(val || 0).toLocaleString()}` },
    { field: "paid_amount", label: "Paid", width: "130px", align: "right", render: (val) => <span className="text-success">₹{Number(val || 0).toLocaleString()}</span> },
    { field: "due_amount", label: "Due", width: "130px", align: "right", render: (val) => <span className={val > 0 ? 'text-danger fw-bold' : ''}>₹{Number(val || 0).toLocaleString()}</span> },
    { field: "status", label: "Status", width: "120px", align: "center", render: (val) => <Badge bg={getStatusBadge(val)}>{val}</Badge> }
  ];

  // Export Functions
  const prepareDataForExport = () => {
    const records = data.records || [];
    return records.map((row, index) => ({
      "Sr No": index + 1,
      "Invoice #": row.invoice_number,
      "Date": row.invoice_date ? format(parseISO(row.invoice_date), 'MMM dd, yyyy') : '-',
      "Vendor": row.vendor_name || '-',
      "Total Amount": `₹${Number(row.total_amount || 0).toLocaleString()}`,
      "Paid Amount": `₹${Number(row.paid_amount || 0).toLocaleString()}`,
      "Due Amount": `₹${Number(row.due_amount || 0).toLocaleString()}`,
      "Status": row.status
    }));
  };

  const exportToCSV = () => {
    const exportData = prepareDataForExport();
    if (exportData.length === 0) return;
    const headers = Object.keys(exportData[0]).join(",");
    const csvContent = [
      headers,
      ...exportData.map(row => Object.values(row).map(val => `"${val}"`).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `Invoices_Report_${todayStr}.csv`);
    link.click();
  };

  const exportToExcel = () => {
    const exportData = prepareDataForExport();
    if (exportData.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices Report");
    XLSX.writeFile(workbook, `Invoices_Report_${todayStr}.xlsx`);
  };

  const exportToPDF = () => {
    const records = data.records || [];
    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFontSize(18);
    doc.text('Invoices Report', 14, 20);
    doc.setFontSize(12);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);

    const tableData = records.map((row, index) => [
      index + 1,
      row.invoice_number,
      row.invoice_date ? format(parseISO(row.invoice_date), 'MMM dd, yyyy') : '-',
      row.vendor_name || '-',
      `₹${Number(row.total_amount || 0).toLocaleString()}`,
      `₹${Number(row.paid_amount || 0).toLocaleString()}`,
      `₹${Number(row.due_amount || 0).toLocaleString()}`,
      row.status
    ]);

    doc.autoTable({
      head: [['Sr', 'Invoice #', 'Date', 'Vendor', 'Total', 'Paid', 'Due', 'Status']],
      body: tableData,
      startY: 35,
      theme: 'grid',
      headStyles: { fillColor: [13, 110, 253] }, // Blue header
      columnStyles: {
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right', fontStyle: 'bold', textColor: [220, 53, 69] } // Red text for Due
      }
    });

    doc.save(`Invoices_Report_${todayStr}.pdf`);
  };

  if (loading && (!data.records || !data.records.length)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Loading invoices...</span>
      </div>
    );
  }

  return (
    <div className="container-fluid bg-light min-vh-100 pb-5">
      <div className="card shadow-sm border-0 mt-1 mx-2">
        {/* Header */}
        <div className="library-header border shadow-sm mt-3 mb-2 rounded mx-2">
          <Col md={6} className="d-flex align-items-center gap-3 ms-3">
            <button
              onClick={() => navigate('/reports')}
              className="shadow-sm d-flex align-items-center justify-content-center custom-btn-back"
            >
              <i className="fa-solid fa-arrow-left"></i>
            </button>
            <div>
              <h4 className="mb-0 fw-bold fs-7" style={{ color: "var(--primary-color)" }}>
                <FileEarmarkText className="me-2 mb-1" /> Invoices Report
              </h4>
            </div>
          </Col>
          <div className="library-header-right">
            <Dropdown align="end">
              <Dropdown.Toggle variant="light" size="sm">
                <i className="fa fa-download me-1" /> Export
              </Dropdown.Toggle>
              <Dropdown.Menu className="shadow-sm border-0">
                <Dropdown.Item onClick={exportToExcel}>
                  <i className="fa-solid fa-file-excel me-2 text-success" /> Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={exportToCSV}>
                  <i className="fa-solid fa-file-csv me-2 text-info" /> CSV
                </Dropdown.Item>
                <Dropdown.Item onClick={exportToPDF}>
                  <i className="fa-solid fa-file-pdf me-2 text-danger" /> PDF
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Stats Cards Row */}
        {data.stats && Object.keys(data.stats).length > 0 && (
          <div className="px-3 pt-2">
            <Row className="g-3">
              {/* <Col md={3}>
                <Card body className="text-center h-100 shadow-sm border-0" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="text-muted mb-1 small fw-semibold">Total Invoices</div>
                  <div className="h4 fw-bold text-primary mb-0">{data.stats.total_invoices || 0}</div>
                </Card>
              </Col> */}
              <Col md={4}>
                <Card body className="text-center h-100 shadow-sm border-0" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="text-muted mb-1 small fw-semibold">Total Amount</div>
                  <div className="h4 fw-bold text-success mb-0">₹{data.stats.total_amount?.toLocaleString() || 0}</div>
                </Card>
              </Col>
              <Col md={4}>
                <Card body className="text-center h-100 shadow-sm border-0" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="text-muted mb-1 small fw-semibold">Total Paid</div>
                  <div className="h4 fw-bold text-info mb-0">₹{data.stats.total_paid?.toLocaleString() || 0}</div>
                </Card>
              </Col>
              <Col md={4}>
                <Card body className="text-center h-100 shadow-sm border-0" style={{ backgroundColor: '#fff3cd' }}>
                  <div className="text-muted mb-1 small fw-semibold">Total Due</div>
                  <div className="h4 fw-bold text-danger mb-0">₹{data.stats.total_due?.toLocaleString() || 0}</div>
                </Card>
              </Col>
            </Row>
          </div>
        )}

        {/* Filters */}
        <div className="p-3 bg-white mt-1 border-bottom">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={2}>
              <div style={labelStyle}><Calendar3 /> Time Period</div>
              <Form.Select style={inputBaseStyle} value={filters.days} onChange={(e) => handleFilterChange("days", e.target.value)}>
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="365">Last Year</option>
                <option value="all">All Time</option>
                <option value="custom">Custom Range</option>
              </Form.Select>
            </Col>
            
            {filters.days === "custom" && (
              <>
                <Col xs={12} md={2}>
                  <div style={labelStyle}><Calendar3 /> From</div>
                  <Form.Control type="date" style={inputBaseStyle} value={filters.from_date} onChange={(e) => handleFilterChange("from_date", e.target.value)} />
                </Col>
                <Col xs={12} md={2}>
                  <div style={labelStyle}><Calendar3 /> To</div>
                  <Form.Control type="date" style={inputBaseStyle} value={filters.to_date} onChange={(e) => handleFilterChange("to_date", e.target.value)} />
                </Col>
              </>
            )}

            <Col xs={12} md={2}>
              <div style={labelStyle}><Filter /> Status</div>
              <Form.Select style={inputBaseStyle} value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)}>
                <option value="">All Statuses</option>
                <option value="UNPAID">Unpaid</option>
                <option value="PARTIAL">Partial</option>
                <option value="PAID">Paid</option>
                <option value="CANCELLED">Cancelled</option>
              </Form.Select>
            </Col>

            <Col xs={12} md={3}>
              <div style={labelStyle}>
                <Search /> Search 
                {searchInput !== filters.searchTerm && searchInput.length >= 3 && (
                  <Spinner animation="border" size="sm" className="ms-2 text-primary" />
                )}
              </div>
              <Form.Control 
                style={inputBaseStyle} 
                placeholder="Invoice # or Vendor (Min 3 chars)" 
                value={searchInput} 
                onChange={(e) => setSearchInput(e.target.value)} 
              />
            </Col>

            <Col xs="auto">
              <Button variant="light" onClick={resetFilters} className="border shadow-sm" style={{ height: '44px' }}>
                  <i className="fa-solid fa-undo text-secondary" /> 
              </Button>
            </Col>
          </Row>
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="danger" className="mx-3 mt-3">
            {error}
          </Alert>
        )}

        {/* Table */}
        <ResizableTable
          data={data.records || []}
          columns={columns}
          loading={loading}
          currentPage={currentPage}
          recordsPerPage={15}
          onPageChange={setCurrentPage}
          showSerialNumber={true}
          showActions={false}
          emptyMessage="No invoices found. Try adjusting your filters."
        />

        {/* Loading Overlay */}
        {loading && data.records && data.records.length > 0 && (
          <div className="position-fixed top-50 start-50 translate-middle z-index-1050">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2 text-primary fw-bold">Updating data...</div>
          </div>
        )}

      </div>
    </div>
  );
};

export default InvoicesReport;